import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RiskBadge } from "@/components/RiskBadge";
import { GuestTags } from "@/components/GuestTags";
import { formatTime } from "@/utils/formatters";
import { ClockCountdown, CaretLeft, CaretRight, WifiHigh, WifiSlash, Wrench, Car, Door } from "@phosphor-icons/react";
import { useWs } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import api from "@/lib/api";

const PAGE_SIZE = 12;

function ValidationRow({ validation, index, timezone, riskLabels, customLevels, isNew, onEditPlate, onRoomClick }) {
  const isOverLimit = validation.over_limit;
  return (
    <TableRow
      className={`border-b border-[#1A1A1A] hover:bg-[#1A1A1A] transition-colors duration-150 ${
        isOverLimit ? "bg-[#FF3B30]/5 border-l-2 border-l-[#FF3B30]" : ""
      } ${isNew ? "animate-ws-highlight" : ""}`}
      data-testid={`validation-row-${index}`}
    >
      <TableCell className="font-mono-data text-sm text-white tracking-wider">
        {validation.car_plate}
        {Array.from({ length: Math.min(validation.plate_edit_count || 0, 2) }).map((_, i) => (
          <Car key={`edit-${i}`} size={12} weight="fill" className="inline ml-0.5 text-[#FF9F0A]" />
        ))}
      </TableCell>
      <TableCell className="font-mono-data text-sm">
        <button onClick={() => onRoomClick(validation.room_number)} className="text-[#A3A3A3] hover:text-[#007AFF] hover:underline transition-colors" data-testid={`room-click-${index}`}>
          {validation.room_number}
        </button>
        <GuestTags vip={validation.guest_vip} rateCode={validation.guest_rate_code} />
      </TableCell>
      <TableCell className="font-mono-data text-sm text-[#007AFF] text-center">{validation.room_validated_count ?? "-"}</TableCell>
      <TableCell className="text-sm text-[#A3A3A3] cursor-default uppercase" title={validation.user_name}>{validation.user_username || validation.user_name}</TableCell>
      <TableCell className="font-mono-data text-xs text-[#737373]">{formatTime(validation.timestamp, timezone)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <RiskBadge status={validation.risk_status} score={validation.risk_score} labels={riskLabels} customLevels={customLevels} />
          <button
            onClick={() => onEditPlate(validation)}
            disabled={(validation.plate_edit_count || 0) >= 2}
            className={`p-1 rounded-sm transition-colors ${(validation.plate_edit_count || 0) >= 2 ? "text-[#404040] cursor-not-allowed" : "text-[#737373] hover:text-[#FF9F0A] hover:bg-[#FF9F0A]/10"}`}
            data-testid={`edit-plate-${index}`}
            title={(validation.plate_edit_count || 0) >= 2 ? "Max 2 edits reached" : "Edit car plate"}
          >
            <Wrench size={14} weight="bold" />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function LiveRecordsTable({ validations, timezone = "UTC", riskLabels = {}, customLevels = null, newIds = new Set(), onPlateUpdated }) {
  const [page, setPage] = useState(0);
  const [editDialog, setEditDialog] = useState(null);
  const [newPlate, setNewPlate] = useState("");
  const [saving, setSaving] = useState(false);
  const [roomDialog, setRoomDialog] = useState(null);
  const [roomValidations, setRoomValidations] = useState([]);
  const [roomLoading, setRoomLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(validations.length / PAGE_SIZE));
  const paged = validations.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (page >= totalPages && page > 0) setPage(0);

  const ws = useWs();
  const isLive = ws?.connected;

  const handleEditPlate = (v) => {
    setEditDialog(v);
    setNewPlate(v.car_plate);
  };

  const handleRoomClick = async (room) => {
    setRoomDialog(room);
    setRoomLoading(true);
    try {
      const { data } = await api.get(`/validations/by-room?room=${room}&limit=50`);
      setRoomValidations(data.validations);
    } catch (err) {
      console.error("Failed to load room validations:", err);
      setRoomValidations([]);
    } finally {
      setRoomLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!editDialog || !newPlate.trim()) return;
    const plate = newPlate.trim().toUpperCase();
    if (plate === editDialog.car_plate) {
      setEditDialog(null);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/validations/${editDialog.id}/plate`, { car_plate: plate });
      toast.success(data.message);
      setEditDialog(null);
      if (onPlateUpdated) onPlateUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update plate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            <ClockCountdown size={20} weight="bold" className="text-[#007AFF]" />
            LIVE RECORDS
            {isLive ? (
              <span className="flex items-center gap-1 text-[10px] font-normal text-[#32D74B] uppercase tracking-wider ml-2" data-testid="ws-live-indicator">
                <WifiHigh size={12} weight="bold" className="animate-pulse" /> Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-normal text-[#737373] uppercase tracking-wider ml-2" data-testid="ws-offline-indicator">
                <WifiSlash size={12} weight="bold" /> Offline
              </span>
            )}
          </CardTitle>
          <span className="text-xs text-[#737373] font-mono-data">{validations.length} entries</span>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold">Car Plate</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold">Room</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold text-center">Validated Count</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold">Operator</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold">Time</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] font-semibold">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={6} className="text-center py-12 text-[#737373] text-sm">
                  No validations yet. Start by validating a car.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((v, i) => (
                <ValidationRow key={v.id} validation={v} index={page * PAGE_SIZE + i} timezone={timezone} riskLabels={riskLabels} customLevels={customLevels} isNew={newIds.has(v.id)} onEditPlate={handleEditPlate} onRoomClick={handleRoomClick} />
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#262626]">
            <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}
              className="text-[#A3A3A3] hover:text-white h-7 px-2" data-testid="live-prev-page">
              <CaretLeft size={14} className="mr-1" /> Prev
            </Button>
            <span className="text-xs text-[#737373] font-mono-data" data-testid="live-page-info">
              Page {page + 1} of {totalPages}
            </span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}
              className="text-[#A3A3A3] hover:text-white h-7 px-2" data-testid="live-next-page">
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        )}
      </CardContent>

      {/* Room Detail Dialog */}
      <Dialog open={!!roomDialog} onOpenChange={(open) => { if (!open) setRoomDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-lg" data-testid="room-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Door size={20} weight="bold" className="text-[#007AFF]" />
              ROOM {roomDialog} — VALIDATED CARS
            </DialogTitle>
          </DialogHeader>
          {roomLoading ? (
            <p className="text-sm text-[#737373] py-4 text-center">Loading...</p>
          ) : roomValidations.length === 0 ? (
            <p className="text-sm text-[#737373] py-4 text-center">No validations found for this room.</p>
          ) : (
            <div className="overflow-auto max-h-[50vh]">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#262626] hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Car Plate</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Operator</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Time</TableHead>
                    <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roomValidations.map((v) => (
                    <TableRow key={v.id} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
                      <TableCell className="font-mono-data text-sm text-white tracking-wider">
                        {v.car_plate}
                        {Array.from({ length: Math.min(v.plate_edit_count || 0, 2) }).map((_, i) => (
                          <Car key={`re-${i}`} size={12} weight="fill" className="inline ml-0.5 text-[#FF9F0A]" />
                        ))}
                      </TableCell>
                      <TableCell className="text-sm text-[#A3A3A3] uppercase" title={v.user_name}>{v.user_username || v.user_name}</TableCell>
                      <TableCell className="font-mono-data text-xs text-[#737373]">{formatTime(v.timestamp, timezone)}</TableCell>
                      <TableCell><RiskBadge status={v.risk_status} score={v.risk_score} labels={riskLabels} customLevels={customLevels} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-[#737373] px-4 py-2">{roomValidations.length} total validation(s)</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoomDialog(null)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Plate Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(open) => { if (!open) setEditDialog(null); }}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-sm max-w-sm" data-testid="edit-plate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-white flex items-center gap-2">
              <Wrench size={20} weight="bold" className="text-[#FF9F0A]" />
              EDIT CAR PLATE
            </DialogTitle>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-4">
              <div className="bg-[#0A0A0A] border border-[#262626] rounded-sm p-3">
                <p className="text-xs text-[#737373]">Room: <span className="font-mono-data text-white">{editDialog.room_number}</span></p>
                <p className="text-xs text-[#737373] mt-1">Current Plate: <span className="font-mono-data text-white">{editDialog.car_plate}</span></p>
                <p className="text-xs text-[#737373] mt-1">Edits: <span className="font-mono-data text-white">{editDialog.plate_edit_count || 0}/2</span></p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">New Car Plate</label>
                <Input
                  value={newPlate}
                  onChange={(e) => setNewPlate(e.target.value.toUpperCase())}
                  className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data text-lg tracking-wider"
                  data-testid="edit-plate-input"
                  autoFocus
                />
              </div>
              {newPlate.trim().toUpperCase() !== editDialog.car_plate && newPlate.trim() && (
                <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 rounded-sm p-3 text-xs text-[#FF9F0A]">
                  Plate will change from <span className="font-mono-data font-bold">{editDialog.car_plate}</span> to <span className="font-mono-data font-bold">{newPlate.trim().toUpperCase()}</span>. This change will be logged in the audit trail.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}
              className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm" data-testid="edit-plate-cancel">
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={saving || !newPlate.trim() || newPlate.trim().toUpperCase() === editDialog?.car_plate}
              className="bg-[#FF9F0A] hover:bg-[#FF9F0A]/80 text-black rounded-sm" data-testid="edit-plate-confirm">
              {saving ? "Saving..." : "Confirm Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
